import React, { useState } from 'react';
import { syncTrackingApi, type SyncTrackingItem } from '../../api/syncTrackingApi';
import { CopyableId } from '../shared/CopyableId';
import {
    statusView, dirView, failBucket, stageLabel, stageIndex, STAGE_COUNT,
    eventView, triggerLabel, recordSub, recordRef, entityTypeLabel, isPatientEntity, isAppointmentEntity,
    formatTimestamp,
} from '../../lib/syncTrackingMeta';

const PatientIcon = (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="3.4" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </svg>
);
const ApptIcon = (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4.5" width="18" height="16" rx="2.5" /><path d="M3 9h18M8 2.5v4M16 2.5v4" />
    </svg>
);

const Arrow = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B0B8C2" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);

const ACKNOWLEDGEABLE = new Set(['blocked', 'retryable_failed', 'dead_letter', 'cancelled']);

interface Props {
    item: SyncTrackingItem;
    locationId: string;
    emr: string;
    onClose: () => void;
    onAcknowledged: () => void;
}

export const SyncTrackingDrawer: React.FC<Props> = ({ item, locationId, emr, onClose, onAcknowledged }) => {
    const sv = statusView(item.status);
    const dv = dirView(item.direction);
    const hasErr = !!item.failure_code;
    const fb = hasErr ? failBucket(item.failure_code) : null;
    const idx = stageIndex(item.stage);
    const canAcknowledge = ACKNOWLEDGEABLE.has(item.status);
    const existingComments = item.comments || '';

    const [comment, setComment] = useState('');
    const [acking, setAcking] = useState(false);
    const [ackError, setAckError] = useState<string | null>(null);

    const meta: { k: string; v: string; copyable: boolean }[] = [
        { k: 'Sync item ID', v: `#${item.id}`, copyable: true },
        { k: 'EMR ID', v: item.emr_id || '—', copyable: !!item.emr_id },
        { k: 'Advital ID', v: item.ghl_id || '—', copyable: !!item.ghl_id },
        { k: 'Entity type', v: item.entity_type, copyable: false },
        { k: 'Operation', v: item.operation, copyable: false },
        { k: 'Trigger', v: triggerLabel(item.trigger), copyable: false },
        { k: 'Patient ID', v: item.patient_id != null ? String(item.patient_id) : '—', copyable: item.patient_id != null },
        { k: 'Contact ID', v: item.contact_id != null ? String(item.contact_id) : '—', copyable: item.contact_id != null },
        { k: 'Appointment ID', v: item.appointment_id != null ? String(item.appointment_id) : '—', copyable: item.appointment_id != null },
        { k: 'Sync run', v: item.sync_run_id != null ? `#${item.sync_run_id}` : 'webhook (no run)', copyable: item.sync_run_id != null },
        { k: 'Source version', v: item.source_version || '—', copyable: false },
        { k: 'Dedup key', v: item.deduplication_key || '—', copyable: !!item.deduplication_key },
    ];

    const timeline = item.timeline.filter(e => e.type !== 'heartbeat');

    const handleAcknowledge = async () => {
        setAcking(true);
        setAckError(null);
        try {
            await syncTrackingApi.acknowledge(locationId, emr, item.id, comment.trim());
            onAcknowledged();
        } catch (err) {
            setAckError(err instanceof Error ? err.message : 'Failed to acknowledge');
        } finally {
            setAcking(false);
        }
    };

    return (
        <div
            onClick={onClose}
            className="mapper-ui font-sans fixed inset-0 z-[110] flex justify-end bg-[rgba(24,33,48,0.36)]"
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{ animation: 'mapperDrawerIn .22s ease' }}
                className="w-[520px] max-w-[94vw] h-full bg-[#F8F9FB] shadow-[-14px_0_40px_rgba(24,33,48,0.18)] flex flex-col"
            >
                {/* header */}
                <div className="shrink-0 bg-mapper-white border-b border-mapper-border px-6 py-5">
                    <div className="flex items-start justify-between gap-[14px]">
                        <div className="flex items-center gap-[13px] min-w-0">
                            <div className="w-[42px] h-[42px] rounded-[11px] shrink-0 flex items-center justify-center bg-mapper-border-4">
                                {isAppointmentEntity(item.entity_type) ? ApptIcon : isPatientEntity(item.entity_type) ? PatientIcon : PatientIcon}
                            </div>
                            <div className="min-w-0">
                                <div className="text-[17px] font-extrabold tracking-[-0.3px] truncate">
                                    {entityTypeLabel(item)} · <CopyableId value={recordRef(item)} />
                                </div>
                                <div className="text-[12.5px] text-mapper-muted-2">{recordSub(item)}</div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            title="Close"
                            className="w-[30px] h-[30px] rounded-lg shrink-0 text-mapper-muted-2 flex items-center justify-center text-[20px] hover:bg-mapper-border-4 hover:text-mapper-body-text"
                        >
                            ×
                        </button>
                    </div>
                    <div className="flex items-center gap-[10px] mt-[14px]">
                        <span className={`inline-flex items-center gap-[6px] text-[12.5px] font-bold px-[11px] py-1 rounded-full ${sv.pill}`}>
                            <span className={`w-[7px] h-[7px] rounded-full ${sv.dot}`} style={sv.pulse ? { animation: 'mapperPulse 1.4s ease-in-out infinite' } : undefined} />
                            {sv.label}
                        </span>
                        <div className="flex items-center gap-[5px]">
                            <span className={`text-[11px] font-bold px-2 py-[2px] rounded-md ${dv.fromPill}`}>{dv.from}</span>
                            {Arrow}
                            <span className={`text-[11px] font-bold px-2 py-[2px] rounded-md ${dv.toPill}`}>{dv.to}</span>
                        </div>
                    </div>
                </div>

                {/* scroll body */}
                <div className="flex-1 overflow-y-auto px-6 py-[22px]">
                    {/* error banner */}
                    {hasErr && fb && (
                        <div className="border border-[#F0C6CE] bg-[#FEF6F7] rounded-[13px] px-4 py-[15px] mb-5">
                            <div className="flex items-center gap-2 mb-2">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#B23A4E" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v5" /><path d="M12 16.5v.5" /><circle cx="12" cy="12" r="9" /></svg>
                                <span className="font-mono text-[12.5px] font-bold text-mapper-red-dark">{item.failure_code}</span>
                                <span className={`text-[10.5px] font-bold px-2 py-[2px] rounded-full ${fb.pill}`}>{fb.label}</span>
                            </div>
                            <div className="text-[13.5px] text-[#5A2530] leading-[1.5] mb-[6px]">{item.error_message}</div>
                            <div className="text-[12px] text-[#A9707B]">{fb.hint} · <span className="font-mono">{item.error_class}</span></div>
                        </div>
                    )}

                    {/* retry banner */}
                    {item.next_retry_at && (
                        <div className="flex items-center gap-[10px] border border-[#F0DFB8] bg-[#FFFDF7] rounded-[12px] px-[15px] py-3 mb-5">
                            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#C9891A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 4v4h-4" /></svg>
                            <span className="text-[13px] text-[#7A5A15]">Auto-retry scheduled <strong className="font-bold">{formatTimestamp(item.next_retry_at)}</strong></span>
                        </div>
                    )}

                    {/* pipeline summary */}
                    <div className="grid grid-cols-2 gap-3 mb-[22px]">
                        <div className="bg-mapper-white border border-mapper-border rounded-xl px-[15px] py-[13px]">
                            <div className="text-[11px] font-bold tracking-[0.4px] text-mapper-muted-3 mb-[5px]">CURRENT STAGE</div>
                            <div className="text-[14.5px] font-bold">{stageLabel(item.stage)}</div>
                            <div className="text-[11.5px] text-mapper-muted-2 mt-[2px]">Step {idx + 1} of {STAGE_COUNT}</div>
                        </div>
                        <div className="bg-mapper-white border border-mapper-border rounded-xl px-[15px] py-[13px]">
                            <div className="text-[11px] font-bold tracking-[0.4px] text-mapper-muted-3 mb-[5px]">ATTEMPTS</div>
                            <div className="font-mono text-[14.5px] font-bold">{item.attempt_count} / {item.max_attempts}</div>
                            <div className="text-[11.5px] text-mapper-muted-2 mt-[2px]">of max allowed</div>
                        </div>
                    </div>

                    {/* event history */}
                    <div className="text-[12px] font-bold tracking-[0.4px] text-mapper-muted-3 mb-[14px]">EVENT HISTORY</div>
                    <div className="mb-6">
                        {timeline.map((e, n) => {
                            const ev = eventView(e);
                            const isLast = n === timeline.length - 1;
                            return (
                                <div key={n} className="flex gap-[14px]">
                                    <div className="flex flex-col items-center shrink-0 pt-[2px]">
                                        <span className={`w-3 h-3 rounded-full border-[2.5px] border-white shadow-[0_0_0_1.5px_#E2E6EB] ${ev.dot}`} />
                                        {!isLast && <span className="flex-1 w-[2px] bg-mapper-border-5 my-1" />}
                                    </div>
                                    <div className="flex-1 min-w-0 pb-[18px]">
                                        <div className="flex items-baseline justify-between gap-[10px]">
                                            <span className="text-[13.5px] font-bold text-mapper-text">{ev.title}</span>
                                            <span className="text-[11.5px] text-mapper-muted-4 whitespace-nowrap">{formatTimestamp(e.at)}</span>
                                        </div>
                                        {e.msg && <div className="text-[12.5px] text-mapper-muted leading-[1.45] mt-[3px]">{e.msg}</div>}
                                        {(e.code || e.attempt) && (
                                            <div className="flex gap-[6px] mt-[6px]">
                                                {e.code && <span className="font-mono text-[10.5px] font-semibold px-[7px] py-[1px] rounded-[5px] bg-mapper-red-bg text-mapper-red">{e.code}</span>}
                                                {e.attempt && <span className="text-[10.5px] font-semibold px-[7px] py-[1px] rounded-[5px] bg-mapper-gray-bg text-mapper-body-text-2">attempt {e.attempt}</span>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* record details */}
                    <div className="text-[12px] font-bold tracking-[0.4px] text-mapper-muted-3 mb-3">RECORD DETAILS</div>
                    <div className="bg-mapper-white border border-mapper-border rounded-[13px] overflow-hidden">
                        {meta.map((m, i) => (
                            <div key={i} className="flex items-center justify-between gap-[14px] px-[15px] py-[10px] border-b border-mapper-border-4 last:border-b-0">
                                <span className="text-[12.5px] text-mapper-muted-2">{m.k}</span>
                                {m.copyable ? (
                                    <CopyableId value={m.v} className="font-mono text-[12.5px] font-semibold text-mapper-body-text-3 text-right break-all" />
                                ) : (
                                    <span className="font-mono text-[12.5px] font-semibold text-mapper-body-text-3 text-right break-all">{m.v}</span>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* read-only comments on already-acknowledged (or previously noted) items */}
                    {!canAcknowledge && existingComments && (
                        <div className="mt-5">
                            <div className="text-[12px] font-bold tracking-[0.4px] text-mapper-muted-3 mb-3">COMMENTS</div>
                            <div className="bg-mapper-white border border-mapper-border rounded-[13px] px-[15px] py-[13px] text-[13.5px] text-mapper-body-text leading-[1.5] whitespace-pre-wrap">
                                {existingComments}
                            </div>
                        </div>
                    )}
                </div>

                {/* acknowledge footer — Needs attention / Failed only */}
                {canAcknowledge && (
                    <div className="shrink-0 bg-mapper-white border-t border-mapper-border px-6 py-4">
                        <label className="block text-[12px] font-bold tracking-[0.4px] text-mapper-muted-3 mb-2">
                            COMMENT <span className="font-medium text-mapper-muted-4">(optional)</span>
                        </label>
                        <textarea
                            value={comment}
                            onChange={e => setComment(e.target.value)}
                            placeholder="Add a note about why this was acknowledged…"
                            rows={3}
                            disabled={acking}
                            className="w-full box-border px-3 py-[10px] border border-mapper-border-2 rounded-[10px] text-[13.5px] text-mapper-body-text outline-none bg-white resize-y min-h-[72px] mb-3 disabled:opacity-60"
                        />
                        {ackError && (
                            <div className="text-[12.5px] text-mapper-red mb-3">{ackError}</div>
                        )}
                        <button
                            onClick={handleAcknowledge}
                            disabled={acking}
                            className="w-full py-[11px] rounded-[10px] bg-mapper-teal text-white text-[13.5px] font-semibold shadow-mapper-btn disabled:opacity-50"
                        >
                            {acking ? 'Acknowledging…' : 'Acknowledge'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
